/**
 * カードの画像から型番とカード ID をまとめて読み取る（Issue #929）。サーバーが Claude に読ませ、カード ID が実在して型番が
 * そのカードの収録パックに一致したものだけ自動で反映する（登録前は登録、登録済みは空の型番を埋める）。中身は `card_code_reader.py`。
 * 費用がかかるので、走らせる前に枚数を見せて確かめる。読んだことのある画像は次から対象にならない。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';

const KIND = 'card_code_read';
const TARGETS = [
  { key: 'temp', label: '登録前の画像' },
  { key: 'registered', label: '登録済みで型番が空の画像' },
];
const POLL_MS = 3000;

export default function CardCodeReadPanel({ onProgress, onFinished, refreshKey }) {
  const [counts, setCounts] = useState({});
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCounts = useCallback(async () => {
    const entries = await Promise.all(TARGETS.map(async (t) => {
      try {
        return [t.key, (await api.project('GetCardCodeReadTargets', { target: t.key })).count];
      } catch { return [t.key, null]; }
    }));
    setCounts(Object.fromEntries(entries));
  }, []);

  const loadJob = useCallback(async () => {
    try {
      const rows = await api.project('GetExportRequests', { kind: KIND });
      setJob(rows?.[0] ?? null);
      return rows?.[0] ?? null;
    } catch { return null; }
  }, []);

  useEffect(() => { loadCounts(); loadJob(); }, [loadCounts, loadJob, refreshKey]);   // PDF を取り込んだら読み直す

  const running = job?.status === 'processing' || job?.status === 'pending';
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      const now = await loadJob();
      onProgress?.();
      if (now && now.status !== 'processing' && now.status !== 'pending') {
        loadCounts();
        onFinished?.();
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, loadJob, loadCounts, onProgress, onFinished]);

  const start = async (target) => {
    const n = counts[target.key];
    if (!n) return;
    if (!window.confirm(`${target.label} ${n} 枚を Claude で読み取ります（API の利用料がかかります）。始めますか？`)) return;
    setBusy(true);
    try {
      await api.project('CreateCardCodeReadRequest', { target: target.key });
      await loadJob();
    } catch (err) {
      alert('始められませんでした: ' + err.message);
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    try {
      await api.project('CancelExportRequest', { request_id: job.id });
      await loadJob();
      loadCounts();
    } catch (err) { alert('止められませんでした: ' + err.message); }
  };

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-title" style={{ marginBottom: 8 }}>型番とカード ID をまとめて読み取る</div>
      <div className="card-meta" style={{ marginBottom: 12 }}>
        カード ID が実在し、型番がそのカードの収録パックに一致したものだけ自動で登録・型番の記入をします。合わなかったものは下の一覧で直してください。
      </div>
      <div className="actions" style={{ flexWrap: 'wrap' }}>
        {TARGETS.map((t) => (
          <button key={t.key} className="btn btn-primary btn-sm" data-testid={`code-read-${t.key}`}
            disabled={busy || running || !counts[t.key]} onClick={() => start(t)}>
            {t.label}を読む（{counts[t.key] ?? '…'} 枚）
          </button>
        ))}
      </div>
      {job && (
        <div className="card-meta" style={{ marginTop: 12 }} data-testid="code-read-job">
          #{job.id} {running ? '読み取り中' : { completed: '完了', failed: '失敗', cancelled: '止めた' }[job.status] ?? job.status}
          {job.progress && `: ${job.progress}`}
          {job.error_message && <span style={{ color: '#e94560' }}>（{job.error_message}）</span>}
          {running && <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={cancel}>止める</button>}
        </div>
      )}
    </div>
  );
}
